import Http from '@/services/http';
import {
  ModuleSubtype,
  ModuleTypes as moduleTypeEnum,
  SupervisionMenuRequest,
  SupervisionMenuResponse,
} from 'sigma-mappings';
import { pushAlert } from '@/services/messages/alertService';
import router from '@/router';

const urls = {
  base: '/supervisao/menu',
};

export const FIRST_MODULE_ALIAS = 0;

function getMenu(assetId) {
  const http = new Http();
  return http
    .get(urls.base, {
      params: new SupervisionMenuRequest({ assetId }),
    })
    .then(({ data }) => data.map(item => new SupervisionMenuResponse(item)));
}

function handleModuleRoute(route, modulesList) {
  const redirectedEngineeringSubModule = (modulesList.engineeringItems || []).find(
    item => item.subtype === route.query?.redirectedFrom || '',
  );

  const redirectedSupervisionSubModule = (modulesList.supervisionItems || []).find(
    item => item.subtype === route.query?.redirectedFrom || '',
  );

  if (redirectedEngineeringSubModule || redirectedSupervisionSubModule) {
    const routeName = redirectedSupervisionSubModule
      ? 'monitoring.supervision.sensors'
      : 'monitoring.engineering.module';

    const extraParams = redirectedSupervisionSubModule
      ? { sensorId: redirectedSupervisionSubModule.id }
      : { assetModuleId: redirectedEngineeringSubModule.id };

    return router.push({
      name: routeName,
      params: {
        ...route.params,
        ...extraParams,
      },
    });
  }

  if ((modulesList.supervisionItems || []).length) {
    const [module] = modulesList.supervisionItems;
    return router.push({
      name: 'monitoring.supervision.sensors',
      params: {
        ...route.params,
        sensorId: module.id,
      },
    });
  }

  if ((modulesList.engineeringItems || []).length) {
    const [module] = modulesList.engineeringItems;
    return router.push({
      name: 'monitoring.engineering.module',
      params: {
        ...route.params,
        assetModuleId: module.id,
      },
    });
  }

  return router.push({
    name: 'monitoring',
    params: { ...route.params },
  });
}

function handleEngineeringRoute(routeParams, moduleList) {
  let { assetModuleId } = routeParams;
  assetModuleId = typeof assetModuleId === 'string' ? assetModuleId.toUpperCase() : assetModuleId;
  const toFirstModule = assetModuleId === FIRST_MODULE_ALIAS;
  const moduleData = moduleList
    .find(({ id }) => id === assetModuleId || toFirstModule);

  if (!moduleData) {
    router.push({
      name: 'engineering',
      params: {
        installationId: routeParams.installationId,
      },
    });
    return null;
  }

  if (toFirstModule) {
    router.push({
      name: 'monitoring.engineering.module',
      params: {
        ...routeParams,
        assetModuleId: moduleData.id,
      },
    });
    return null;
  }

  return moduleData;
}

function handleSensorRoute(routeParams, sensorsList) {
  let { sensorId } = routeParams;
  sensorId = typeof sensorId === 'string' ? sensorId.toUpperCase() : sensorId;
  const toFirstSensor = sensorId === FIRST_MODULE_ALIAS;
  const sensorData = sensorsList
    .find(({ id }) => id === sensorId || toFirstSensor);

  if (!sensorData) {
    if (toFirstSensor) {
      router.push({
        name: 'monitoring.supervision.digital-network',
        params: {
          ...routeParams,
        },
      });
      return null;
    }

    router.push({
      name: 'supervision',
      params: {
        ...routeParams,
      },
    });
    return null;
  }

  if (toFirstSensor) {
    router.push({
      name: 'monitoring.supervision.sensors',
      params: {
        ...routeParams,
        sensorId: sensorData.id,
      },
    });
    return null;
  }

  return sensorData;
}

function menuMapped(supervisionMapped, engineeringMapped) {
  const separator = {
    type: 'separator',
  };

  const supervisionGroup = {
    groupTitleLabel: 'my-sigma.side-menu.supervision',
    groupSubTitleLabel: 'my-sigma.side-menu.sensors',
    limitItemsLength: 0,
  };

  const engineeringGroup = {
    groupTitleLabel: 'my-sigma.side-menu.engineering',
    limitItemsLength: 0,
    items: engineeringMapped,
  };

  if (supervisionMapped.length > 0 && engineeringMapped.length > 0) {
    return [
      {
        ...supervisionGroup,
        items: [
          ...supervisionMapped,
          separator,
        ],
      },
      {
        ...engineeringGroup,
        items: [...engineeringMapped],
      },
    ];
  }

  if (supervisionMapped.length > 0) {
    return [
      {
        ...supervisionGroup,
        items: [
          ...supervisionMapped,
        ],
      },
    ];
  }

  return [
    {
      ...engineeringGroup,
      items: [
        ...engineeringMapped,
      ],
    },
  ];
}

const moduleSubtypeApiValues = Object.values(ModuleSubtype);

export default {
  async getMenuItems(assetId) {
    return getMenu(assetId);
  },
  mapSubMenus(assetId, items) {
    const filteredItems = items.filter(({ subtype }) => moduleSubtypeApiValues.includes(subtype));

    const supervisionItems = filteredItems.filter(x => x.moduleType === moduleTypeEnum.SUPERVISION);
    const engineeringItems = filteredItems.filter(x => x.moduleType === moduleTypeEnum.ENGINEERING);

    const supervisionMapped = supervisionItems.map(x => ({
      staticTitle: x.name,
      routeName: 'monitoring.supervision.sensors',
      params: {
        assetId,
        sensorId: x.id,
      },
    }));

    const engineeringMapped = engineeringItems.map(x => ({
      staticTitle: x.name,
      routeName: 'monitoring.engineering.module',
      params: {
        assetId,
        assetModuleId: x.id,
      },
    }));

    return menuMapped(supervisionMapped, engineeringMapped);
  },
  mapEngineeringModuleAndSensorList(items) {
    this.checkSupervisionItems(items);

    const supervisionItems = items.filter(
      x => x.moduleType === moduleTypeEnum.SUPERVISION,
    );

    const engineeringItems = items.filter(
      x => x.moduleType === moduleTypeEnum.ENGINEERING
      && moduleSubtypeApiValues.includes(x.subtype),
    );

    return {
      supervisionItems,
      engineeringItems,
    };
  },
  mapEngineeringModuleList(items) {
    return items.filter(({ subtype }) => moduleSubtypeApiValues.includes(subtype));
  },
  checkSupervisionItems(sensors) {
    const supervisionItems = sensors.filter(
      x => x.moduleType === moduleTypeEnum.SUPERVISION,
    );

    if (supervisionItems.some(sensor => !sensor.features.length)) {
      pushAlert({ code: 'ME022' });
      throw new Error(`${this.$t('supervision.no-functionalities-sensors')}`);
    }
  },
  verifyModuleRoute(route, modulesList) {
    return handleModuleRoute(route, modulesList);
  },
  verifySensorRoute(route, sensorsList) {
    if (route.name === 'monitoring.supervision.sensors') {
      return handleSensorRoute(route.params, sensorsList);
    }
    return null;
  },
  verifyEngineeringRoute(route, moduleList) {
    return handleEngineeringRoute(route.params, moduleList);
  },
};
